import React, { useCallback } from 'react';
import { AdjustTimeEntryRequest } from '../../../components/AutoBreaks/shared';
import {
  calculateTimeEntryStats,
  isLegalTimeEntryStats,
} from '../../../components/AutoBreaks/shared/autoBreaksCalculation/helpers';
import { TimeTrackingBreak } from '../../../components/AutoBreaks/suggestBreaks/clockoutBreaksReducer';
import {
  useSuggestBreakRules,
  useTimeTrackingState,
} from '../../../components/AutoBreaks/suggestBreaks/suggestBreakHooks';
import { SuggestBreaksModal } from '../../../components/AutoBreaks/suggestBreaks/SuggestBreaksModal';
import { useAutoBreaksTimeTrackingFeatures } from '../../../components/AutoBreaks/useAutoBreaksTimeTrackingFeatures';
import { useStopTimeTracking } from '../../../hooks/timeTracking';
import {
  ExtraFieldInput,
  TimeEntryBreakInput,
} from '../../../__generated__/graphql';
import { ExtraFieldInternalValue } from '../../../components/ExtraFields/extraFieldInternalValue';
import { ExtraFieldContextParam } from '../../../components/ExtraFields/types';

import StopTrackingModal from './StopTimeTrackingModal';

type Props = {
  notes: string | null;
  clockOutDate: Date | null;
  onClose: () => void;
  extraFieldValues: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  contextParams: ExtraFieldContextParam[];
};

export const ClockoutModals: React.FC<Props> = ({
  notes,
  clockOutDate,
  onClose,
  extraFieldValues,
  recordTypeName,
  contextParams,
}) => {
  const timeTrackingFeatures = useAutoBreaksTimeTrackingFeatures();

  const suggestBreaksVisible =
    timeTrackingFeatures.suggestBreaks || timeTrackingFeatures.deductBreaks;

  const { timeTrackingState, currentTimeEntryId } = useTimeTrackingState();
  const breakRules = useSuggestBreakRules();

  const { stopTimeTracking } = useStopTimeTracking({
    onSuccess: () => {},
  });

  const handleClockoutSubmit = useCallback(
    (notes: string | null, extraFields: ExtraFieldInput[]) => {
      if (!currentTimeEntryId) {
        return Promise.resolve();
      }
      return stopTimeTracking(
        currentTimeEntryId,
        notes ?? undefined,
        undefined,
        extraFields,
      );
    },
    [currentTimeEntryId, stopTimeTracking],
  );

  const handleSubmitSuggestBreakModal = useCallback(
    (
      notes: string | null,
      breaks: TimeTrackingBreak[] | null,
      extraFields: ExtraFieldInput[],
    ) => {
      if (!currentTimeEntryId) {
        return Promise.resolve();
      }
      return stopTimeTracking(
        currentTimeEntryId,
        notes ?? undefined,
        breaks ? breaks.map(mapToBreakInput) : [],
        extraFields,
      );
    },
    [currentTimeEntryId, stopTimeTracking],
  );

  if (!clockOutDate || !currentTimeEntryId) {
    return null;
  }

  if (!suggestBreaksVisible) {
    return (
      <StopTrackingModal
        timeEntryId={currentTimeEntryId}
        onClose={onClose}
        show={clockOutDate !== null}
        onSubmit={handleClockoutSubmit}
        notes={notes}
        extraFieldValues={extraFieldValues}
        recordTypeName={recordTypeName}
        contextParams={contextParams}
      />
    );
  }

  if (!currentTimeEntryId || !breakRules || !timeTrackingState) {
    return null;
  }

  const adjustTimeEntryRequest: AdjustTimeEntryRequest = {
    start: timeTrackingState.start,
    end: clockOutDate ?? new Date(),
    breaks: timeTrackingState.breaks.map((x) => ({
      start: x.start,
      end: x.end,
      breakId: x.uniqueId,
    })),
  };

  const timeEntryStats = calculateTimeEntryStats(
    breakRules,
    adjustTimeEntryRequest,
  );

  if (!isLegalTimeEntryStats(timeEntryStats)) {
    return (
      <SuggestBreaksModal
        breakRules={breakRules}
        notes={notes}
        timeTrackingFeatures={timeTrackingFeatures}
        timeTrackingState={timeTrackingState}
        onClose={onClose}
        onProceed={handleSubmitSuggestBreakModal}
        extraFieldValues={extraFieldValues}
        recordTypeName={recordTypeName}
        contextParams={contextParams}
      />
    );
  }

  return (
    <StopTrackingModal
      timeEntryId={currentTimeEntryId}
      onClose={onClose}
      show={clockOutDate !== null}
      notes={notes}
      onSubmit={handleClockoutSubmit}
      extraFieldValues={extraFieldValues}
      recordTypeName={recordTypeName}
      contextParams={contextParams}
    />
  );
};

const mapToBreakInput = (src: TimeTrackingBreak): TimeEntryBreakInput => ({
  breakId: src.breakId,
  startDatetime: src.start.toISOString(),
  endDatetime: src.end.toISOString(),
});
